import { useId } from 'react';
import type { LueftungFeld } from '../../api';
import { farbklasse } from '../../Farbregel';
import { FormSection, StatusBadge } from '../../muster';
import { feldBeschriftung, feldgruppen } from './bestandregeln';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EIN FELD DES LÜFTUNGSKATALOGS (TV6c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bewusst NICHT das Muster `Feld` (TX-STANDARD S1.10 Nr. 4), sondern ein
 * eigenes Feld auf dessen Klassen (`m-feld`, `m-feld-beschriftung`,
 * `m-feld-eingabe`, `m-feld-hinweis`) mit Beschriftung über `label for`:
 *
 * 1. Der Schreibweg bleibt Zeichen für Zeichen der alte. Ein Zahlfeld ist ein
 *    `input type="number"` und liefert den Text des Browsers („1800.5“); das
 *    Muster liest deutsch (`leseZahl`) und schriebe „1.800,5“ als 1800,5 - für
 *    Werte, die das Modell und die Tabelle als Text tragen, eine Änderung des
 *    Schreibwegs, die TV6c nicht machen darf (Vorgabe main 20.10.).
 * 2. Die Farbe des Felds sagt, ob der Wert ins Modell zurückgeht
 *    (`farbklasse`, PB52); das Muster kennt sie nicht.
 *
 * Vorgeschlagen als Ausnahme für TX (Befund §20.6), bis das Muster ein
 * Textzahlfeld und die Rückschreibfarbe kennt.
 */
export function Lueftungsfeld({ feld, wert, herkunft, setzen }: {
  feld: LueftungFeld;
  wert: string;
  herkunft?: string;
  setzen: (wert: string) => void;
}) {
  const id = useId();
  const hinweisId = `${id}-hinweis`;
  // Hellgrün: Das Feld geht beim Rückschreiben ins Modell zurück; hellgelb: Es bleibt in der Datenbank.
  const art = farbklasse(feld.gehtInsModell);
  const gemeinsam = {
    id,
    className: 'm-feld-eingabe',
    'aria-describedby': feld.revitParameter ? hinweisId : undefined,
  } as const;

  return (
    <div className={`m-feld lue-feld ${art}`}>
      <label className="m-feld-beschriftung" htmlFor={id}>
        {feldBeschriftung(feld)}
        {feld.istPflicht && <span className="m-feld-pflicht"> *</span>}
      </label>

      {feld.dataType === 'link' ? (
        // Ein Verweis wird eingegeben und geöffnet - als Zeichenkette allein klickt ihn niemand an.
        <span className="lue-feld-verweis">
          <input {...gemeinsam} type="url" value={wert} placeholder="https://…" onChange={(e) => setzen(e.target.value)} />
          {wert && <a href={wert} target="_blank" rel="noreferrer">öffnen</a>}
        </span>
      ) : feld.dataType === 'ja_nein' ? (
        <select {...gemeinsam} value={wert} onChange={(e) => setzen(e.target.value)}>
          <option value="">—</option>
          <option value="true">ja</option>
          <option value="false">nein</option>
        </select>
      ) : feld.dataType === 'auswahl' && feld.options ? (
        <select {...gemeinsam} value={wert} onChange={(e) => setzen(e.target.value)}>
          <option value="">—</option>
          {feld.options.split('|').map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          {...gemeinsam}
          type={feld.dataType === 'zahl' ? 'number' : 'text'}
          value={wert}
          onChange={(e) => setzen(e.target.value)}
        />
      )}

      {(feld.revitParameter || herkunft === 'vorlage' || feld.istPflicht) && (
        <span className="m-feld-hinweis" id={hinweisId}>
          {herkunft === 'vorlage' && <><StatusBadge zustand="neutral" text="aus Vorlage" klein />{' '}</>}
          {feld.istPflicht && 'für die Abnahme nötig. '}
          {/* Der Pfeil zeigt den Zielparameter - und sagt, ob der Wert dort ankommt. */}
          {feld.revitParameter && (feld.gehtInsModell
            ? `→ ${feld.revitParameter}`
            : `${feld.revitParameter} — wird nur gelesen`)}
        </span>
      )}
    </div>
  );
}

/** Die Felder eines Bauteils oder einer Anlage, nach Fachgruppe in der Reihenfolge des Katalogs. */
export function Feldgruppen({ felder, werte, herkunft, setzen }: {
  felder: LueftungFeld[];
  werte: Record<string, string>;
  herkunft: Record<string, string>;
  setzen: (schluessel: string, wert: string) => void;
}) {
  return (
    <>
      {feldgruppen(felder).map((g) => (
        <FormSection key={g.name} titel={g.name} spalten={3}>
          {g.felder.map((f) => (
            <Lueftungsfeld
              key={f.fieldKey}
              feld={f}
              wert={werte[f.fieldKey] ?? ''}
              herkunft={herkunft[f.fieldKey]}
              setzen={(v) => setzen(f.fieldKey, v)}
            />
          ))}
        </FormSection>
      ))}
    </>
  );
}
